/**
 * Utility functions for product analytics.
 *
 * NOTE: you must have the FullStory browser SDK installed:
 *     import { init as initFullStory } from '@fullstory/browser';
 *
 * FullStory methods throw an error if `initFullStory` has not run.
 * We only initialize FullStory in production.
 */
import * as Sentry from '@sentry/react';
import { FullStory } from '@fullstory/browser';

type TrackingOptions = {
  elementName?: string;
  unmask?: boolean;
};
type FullstoryAttributes = {
  'data-fs-element'?: string;
  'data-fs-unmask'?: boolean;
};

/**
 * Set FullStory attributes on an element.
 *
 * @param elementName Use this to define an Element. Any events
 * or interactions on this element will be labeled with the elementName.
 * Generally, naming should be in the format of "Page Name: Element Name" so they are
 * globally unique, e.g. "Task Page: Submit Comment Button".
 *
 * @param unmask Use this to unmask the text in this element and all its children.
 * By default, all text is redacted in FullStory. Passing this parameter will un-hide
 * the element's text so it can be seen in FullStory sessions or used in a "watched" element.
 * **IMPORTANT: make sure this element will not contain PHI.**
 *
 * @returns A dictionary of attributes to be spread onto an element.
 * Example usage:
 * ```tsx
 * <Button {...createTrackingProps({ elementName: 'Task Page: Submit Comment Button' })}>
 *   Submit Comment
 * </Button>
 * ```
 */
export const createTrackingProps = ({ elementName, unmask = false }: TrackingOptions) => {
  const props: FullstoryAttributes = {};
  if (elementName) {
    props['data-fs-element'] = elementName;
  }
  if (unmask) {
    props['data-fs-unmask'] = true;
  }
  return props;
};

/**
 * Set FullStory identity for the current user.
 * https://developer.fullstory.com/browser/identification/identify-users/
 */
export const setAnalyticsIdentity = (
  userId: string,
  email: string,
  displayName: string = email,
  // Generally should be snake_case
  additionalProperties: Record<string, string> = {},
) => {
  if (import.meta.env.MODE !== 'production' || !import.meta.env.VITE_FULLSTORY_ORG_ID) {
    return;
  }

  try {
    FullStory('setIdentity', {
      uid: userId,
      properties: {
        email,
        displayName,
        ...additionalProperties,
      },
    });
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
  }
};

/**
 * Reset FullStory identity when user logs out.
 * https://developer.fullstory.com/browser/identification/anonymize-users/
 */
export const resetAnalyticsIdentity = () => {
  if (import.meta.env.MODE !== 'production' || !import.meta.env.VITE_FULLSTORY_ORG_ID) {
    return;
  }

  try {
    FullStory('setIdentity', { anonymous: true });
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
  }
};

/**
 * Set properties on the current page for FullStory.
 * https://developer.fullstory.com/browser/set-page-properties/
 *
 * Note: ONLY set pageName if you need to create a new page in FullStory.
 * This value will take precedence over any URL-based page definitions
 * that may already exist.
 * You generally should not need to set pageName, unless there is a
 * page definition that is difficult to match by URL.
 */
export const setPageProperties = (
  // Generally should be snake_case
  additionalProperties: Record<string, string> = {},
  pageName?: string,
) => {
  if (import.meta.env.MODE !== 'production' || !import.meta.env.VITE_FULLSTORY_ORG_ID) {
    return;
  }

  try {
    FullStory('setProperties', {
      type: 'page',
      properties: {
        pageName,
        ...additionalProperties,
      },
    });
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
  }
};

/**
 * Track a custom event in FullStory.
 * https://developer.fullstory.com/browser/capture-events/analytics-events/
 */
export const trackEvent = (
  eventName: string,
  // Generally should be snake_case
  properties: Record<string, string> = {},
) => {
  if (import.meta.env.MODE !== 'production' || !import.meta.env.VITE_FULLSTORY_ORG_ID) {
    return;
  }

  try {
    FullStory('trackEvent', {
      name: eventName,
      properties,
    });
  } catch (error) {
    console.error(error);
    Sentry.captureException(error);
  }
};

/**
 * Stops analytics recording/capture for the current session.
 * This completely halts data collection until restart is called.
 */
export const stopAnalyticsCapture = () => {
  if (import.meta.env.MODE !== 'production' || !import.meta.env.VITE_FULLSTORY_ORG_ID) {
    return;
  }

  try {
    FullStory('shutdown');
  } catch (error) {
    console.error('Error stopping analytics capture:', error);
    Sentry.captureException(error);
  }
};

/**
 * Restarts analytics recording/capture after it has been stopped.
 * This resumes data collection within the same user session - it does not start
 * a completely new session but continues recording from the point it was restarted.
 */
export const restartAnalyticsCapture = () => {
  if (import.meta.env.MODE !== 'production' || !import.meta.env.VITE_FULLSTORY_ORG_ID) {
    return;
  }

  try {
    FullStory('restart');
  } catch (error) {
    console.error('Error restarting analytics capture:', error);
    Sentry.captureException(error);
  }
};
